import { useFeatureFlagsProviderContext } from '@brightdrop/feature-flags-client';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  buildUserContext,
  USER_CONTEXT_KIND,
} from '~/common/utils/featureFlagsUserContext';
import { selectProfile } from '~/store/profile/profileSlice.selectors';

/**
 * Syncs LaunchDarkly user context with the signed-in profile so that flag
 * targeting (e.g. by user id or org id) reflects the logged-in user, regardless
 * of any impersonation.
 *
 * `setContext` runs only when the payload's content changes, not on every new
 * profile object; `FeatureFlagsResolver` queues until the LD client exists and
 * keeps `isUserContextIdentified` false until that `identify` finishes so
 * targeted flags match the profile.
 *
 * A locale change resets the API caches and refetches `/me`, so the same user
 * arrives again as a new object. Identifying again would drop
 * `isUserContextIdentified` for flags that have not changed, and anything gated
 * on it, such as the console language, would flicker off and change the locale
 * once more.
 */
const FeatureFlagsContextSync = (): null => {
  const { setContext: setFeatureFlagsContext } =
    useFeatureFlagsProviderContext();
  const profile = useSelector(selectProfile);
  const sentPayloadKey = useRef<string | null>(null);

  const contextPayload = useMemo(
    () => buildUserContext(profile ?? undefined),
    [profile]
  );

  useEffect(() => {
    if (!contextPayload) {
      sentPayloadKey.current = null;
      return;
    }
    const payloadKey = JSON.stringify(contextPayload);
    if (payloadKey === sentPayloadKey.current) return;
    sentPayloadKey.current = payloadKey;
    setFeatureFlagsContext(USER_CONTEXT_KIND, contextPayload);
  }, [contextPayload, setFeatureFlagsContext]);

  return null;
};

export default FeatureFlagsContextSync;
